import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { 
  Activity, 
  Trophy, 
  Bell, 
  Home,
  Menu,
  X,
  LogOut,
  User,
  UserCheck,
  Settings,
  Sun,
  Moon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Always prioritize localStorage over system preference
    const savedTheme = localStorage.getItem('pingpal-theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
    // Only use system preference for initial load if no saved preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Initialize theme on component mount
  useEffect(() => {
    const themeName = theme === 'dark' ? 'PingPalDark' : 'PingPal';
    document.documentElement.setAttribute('data-theme', themeName);
    localStorage.setItem('pingpal-theme', theme);
  }, [theme]);

  // Listen for system preference changes (only on initial load)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't made a manual selection
      const savedTheme = localStorage.getItem('pingpal-theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Only add listener if no manual selection exists
    const savedTheme = localStorage.getItem('pingpal-theme');
    if (!savedTheme) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  // Theme toggle function
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const getViewTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/gamification') return 'Gamification';
    if (path === '/alerting') return 'Alerting';
    if (path.startsWith('/monitor/')) return 'Monitor Details';
    if (path === '/profile') return 'Profile';
    if (path === '/admin/users') return 'User Management';
    return 'PingPal';
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="h-screen bg-base-200 flex overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-base-100 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col h-full ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-base-300">
          <div className="flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" />
            <h1 className="text-xl font-bold">PingPal</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden btn btn-ghost btn-sm"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActiveRoute('/dashboard') 
                ? 'bg-primary text-primary-content' 
                : 'hover:bg-base-200'
            }`}
          >
            <Home className="w-5 h-5" />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => navigate('/gamification')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActiveRoute('/gamification') 
                ? 'bg-primary text-primary-content' 
                : 'hover:bg-base-200'
            }`}
          >
            <Trophy className="w-5 h-5" />
            <span>Gamification</span>
          </button>

          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <button
              onClick={() => navigate('/alerting')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActiveRoute('/alerting') 
                  ? 'bg-primary text-primary-content' 
                  : 'hover:bg-base-200'
              }`}
            >
              <Bell className="w-5 h-5" />
              <span>Alerting</span>
            </button>
          )}
        </nav>

        <div className="p-4 border-t border-base-300 space-y-4">
          {/* User Info */}
          <div className="p-3 bg-base-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="avatar placeholder">
                <div className="bg-primary text-primary-content rounded-full w-8">
                  <span className="text-xs">{user?.username.charAt(0).toUpperCase()}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.username}</div>
                <div className="text-xs opacity-70 capitalize">{user?.role}</div>
              </div>
              <div className="dropdown dropdown-top">
                <div tabIndex={0} role="button" className="btn btn-ghost btn-xs">
                  <Settings className="w-4 h-4" />
                </div>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                  <li>
                    <button onClick={() => navigate('/profile')} className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Profile
                    </button>
                  </li>
                  {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <li>
                      <button onClick={() => navigate('/admin/users')} className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        User Management
                      </button>
                    </li>
                  )}
                  <li>
                    <button 
                      onClick={async () => {
                        try {
                          await logout();
                          navigate('/login');
                        } catch (error) {
                          console.error('Logout failed:', error);
                        }
                      }} 
                      className="flex items-center gap-2 text-error"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="text-center text-sm opacity-70">
            PingPal v2.0.0
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:ml-0 min-w-0">
        {/* Header */}
        <header className="bg-base-100 shadow-sm border-b border-base-300 flex-shrink-0">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden btn btn-ghost btn-sm"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold">{getViewTitle()}</h2>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="btn btn-ghost btn-sm"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4" />
                <span>Welcome, {user?.username}</span>
              </div>
              <div className="badge badge-primary">v2.0.0</div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 